import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Background } from '../../shared/components/background/background';
import { Connect } from '../../shared/components/connect/connect';
import { BlogContentService } from '../../shared/services/blog-content.service';
import { IBlogPost } from '../../shared/interfaces/blog-post.interface';
import { SeoService } from '../../shared/services/seo.service';

@Component({
  selector: 'app-blog-post',
  imports: [CommonModule, RouterLink, Background, Connect],
  templateUrl: './blog-post.html',
  styleUrl: './blog-post.scss',
})
export class BlogPost {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private blogContent = inject(BlogContentService);
  private sanitizer = inject(DomSanitizer);
  private seo = inject(SeoService);

  protected post = toSignal(
    this.route.paramMap.pipe(
      switchMap((p) => {
        const slug = p.get('slug');
        if (!slug) return of<IBlogPost | null>(null);
        return this.blogContent.getPost(slug).pipe(catchError(() => of<IBlogPost | null>(null)));
      }),
    ),
    { initialValue: undefined },
  );

  protected sanitizedHtml = computed<SafeHtml | null>(() => {
    const p = this.post();
    return p ? this.sanitizer.bypassSecurityTrustHtml(p.html) : null;
  });

  protected formattedDate = computed(() => {
    const p = this.post();
    if (!p?.date) return '';
    return new Date(p.date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  });

  constructor() {
    // Update meta tags + JSON-LD when the post resolves (SSR + browser).
    effect(() => {
      const p = this.post();
      if (!p) {
        this.seo.removeJsonLd('article');
        this.seo.removeJsonLd('breadcrumb');
        return;
      }
      this.seo.apply({
        title: p.title,
        description: p.excerpt || undefined,
        canonical: `/${p.slug}`,
        image: p.featured_image || undefined,
        imageAlt: p.featured_image_alt || undefined,
        type: 'article',
        publishedTime: p.date,
        modifiedTime: p.modified,
        author: p.author,
      });
      this.seo.setArticle({
        title: p.title,
        description: p.excerpt || '',
        slug: p.slug,
        image: p.featured_image || undefined,
        datePublished: p.date,
        dateModified: p.modified,
        author: p.author,
        section: p.category_names?.[0] || p.categories?.[0],
        wordCount: (p as any).wordCount,
      });
      this.seo.setBreadcrumbs([
        { name: 'Home', url: '/' },
        { name: 'Blog', url: '/blog' },
        { name: p.title },
      ]);
    });
  }
}
